import Paginations from "components/Common/Pagination"
import Spinners from "components/Shared/Spinners"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Table,
} from "reactstrap"
import { getInterviewsScheduleList } from "store/actions/interviewsAction"
import InterviewAppointmentInvitedUsers from "./InterviewAppointmentInvitedUsers"
import InterviewTabs from "./InterviewsTabs/InterviewTabs"
import { usePermissions } from "hooks"
import {
  API_URL,
  formatDateForAPI,
  handleStopPropagation,
} from "components/Common/Utils"
import axios from "axios"
import { setPayload, resetPayload } from "store/reducers/interviewsReducer.js"
import { Info, Pencil, Plus } from "lucide-react"
import { getDropdownData } from "store/actions/commonAction"
import HTMLModal from "pages/MyInterviews/components/HTMLModal"
import AddCandidateInPool from "./AddCandidateInPool"
import { showAlert } from "store/reducers/alertReducer"
import moment from "moment"
import Filters from "components/Shared/Filters"

const Interviews = () => {
  const {
    loading,
    interviewsList,
    perPage,
    totalPages,
    totalCount,
    currentPage,
    payload,
    invitedUserPayload,
    otherTabPayload,
  } = useSelector(state => state.Interviews)

  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  document.title = t("Interviews | Yamama")

  const { permissions, showAll } = usePermissions("interview_schedule")
  const { permissions: interviewEvaluationPermissions } = usePermissions(
    "interview_evaluation"
  )

  const [searchParams, setSearchParams] = useSearchParams()

  const activeTab = searchParams.get("tab") || "INTERVIEW_APPOINTMENT"
  const [isExporting, setIsExporting] = useState(false)
  const [trigger, setTrigger] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [selectedDescription, setSelectedDescription] = useState("")
  const [selectedUuid, setSelectedUuid] = useState(null)
  const [modal, setModal] = useState(false)
  const toggle = () => setModal(!modal)

  useEffect(() => {
    if (activeTab === "INTERVIEW_APPOINTMENT") {
      getAllData()
    }
  }, [activeTab, trigger])

  useEffect(() => {
    fetchDropdownApi()
  }, [])

  const fetchDropdownApi = () => {
    const payload = {
      type: "all",
    }
    dispatch(getDropdownData(payload))
  }

  const handleSearch = () => {
    setTrigger(!trigger)
  }

  const handleReset = () => {
    dispatch(resetPayload())
    setTrigger(!trigger)
  }

  const getAllData = () => {
    const { date_range } = payload?.filters || {}

    let obj = { ...payload }

    const formattedDate =
      date_range?.length === 2
        ? {
            start_date: formatDateForAPI(date_range[0]),
            end_date: formatDateForAPI(date_range[1]),
          }
        : {}

    obj = { ...obj, filters: { ...obj.filters, date_range: formattedDate } }

    dispatch(getInterviewsScheduleList(obj))
    // navigate(`/interview-appointments?page=${page || pageNumber}`)
  }

  const onPageChange = page => {
    dispatch(setPayload({ page_number: page }))
    setTrigger(!trigger)
  }

  const toggleTab = tab => {
    if (activeTab !== tab) {
      setSearchParams({ tab })
    }
  }

  const handleExport = async () => {
    try {
      setIsExporting(true)
      const authUserString = localStorage.getItem("authUser")
      const authUser = authUserString ? JSON.parse(authUserString) : null
      const token = authUser?.token

      let data = {}

      if (activeTab === "INTERVIEW_APPOINTMENT") {
        data = {
          export_type: "interview_appointment_lists",
          filters: {
            keyword: payload?.filters?.keyword || "",
            type: -1,
          },
        }
      } else if (activeTab === "INVITED_USER") {
        data = {
          export_type: "interview_invitation_lists",
          filters: {
            keyword: invitedUserPayload.filters.keyword || "",
            type: 0,
          },
        }
      } else if (activeTab !== "2") {
        data = {
          export_type: "interview_lists",
          filters: {
            keyword: otherTabPayload.filters.keyword || "",
            type: Number(activeTab),
          },
        }
      } else {
        data = {
          export_type: "interview_lists",
          filters: {
            keyword: otherTabPayload.filters.keyword || "",
            type: Number(activeTab),
            is_trial_run:
              otherTabPayload.filters.is_trial_run === "all"
                ? null
                : otherTabPayload.filters.is_trial_run === "yes"
                ? 1
                : 0,
            job_title_ids: otherTabPayload.filters.job_title_ids,
          },
        }
      }

      const response = await axios.post(
        `${API_URL}/api/v1/admin/export/by-type`,
        data,
        {
          headers: {
            Authorization: token, // Attach token in headers
          },
          responseType: "blob", // Important for file downloads
        }
      )

      const contentType = response.headers["content-type"]

      if (contentType === "application/json") {
        return dispatch(
          showAlert({
            text: t("Export initiated, you will be notified once done."),
            type: "success",
          })
        )
      }

      // Create a URL for the downloaded file
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute(
        "download",
        activeTab === "1"
          ? "Scheduled Interview.xlsx"
          : activeTab === "2"
          ? "Interview Completed.xlsx"
          : activeTab === "3"
          ? "Cancelled Interview.xlsx"
          : activeTab === "INTERVIEW_APPOINTMENT"
          ? "Interview Appointment.xlsx"
          : "Invited Users.xlsx"
      ) // Set filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url) // Clean up memory
    } catch (error) {
      console.error("Export failed:", error.response?.data || error.message)
    } finally {
      setIsExporting(false)
    }
  }

  const showCurrentLanguageDescription = () => {
    if (i18n.language === "en") {
      if (
        selectedDescription.en === "<p><br></p>" ||
        selectedDescription.en === ""
      ) {
        return selectedDescription.ar
      } else {
        return selectedDescription.en
      }
    } else {
      if (
        selectedDescription.ar === "<p><br></p>" ||
        selectedDescription.ar === ""
      ) {
        return selectedDescription.en
      } else {
        return selectedDescription.ar
      }
    }
  }

  const showDownloadButton = useMemo(() => {
    if (showAll) return true

    if (activeTab === "INTERVIEW_APPOINTMENT") {
      return permissions.includes("enable_schedule_export")
    } else if (activeTab === "INVITED_USER") {
      return permissions.includes("enable_invitation_export")
    } else if (activeTab === "1" || activeTab === "2" || activeTab === "3") {
      return interviewEvaluationPermissions.includes("enable_interview_export")
    }
  }, [activeTab])

  const filters = {
    keyword: {
      type: "text",
      placeholder: t("Search by title"),
      label: t("Search By"),
      colSize: 4,
    },
    date_range: { type: "dateRange", label: t("Select Date") },
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xl="12">
              <div className="d-flex justify-content-between mb-4 align-items-center">
                <h4 className="card-title ">{t("Interviews")}</h4>
                <div className="d-flex justify-content-end gap-2">
                  {/* {activeTab == 2 && ( */}
                  {showDownloadButton && (
                    <button
                      className="main_btn fit_content"
                      onClick={handleExport}
                      disabled={isExporting}
                    >
                      {t("Download XLSX")}
                    </button>
                  )}
                  {/* )} */}
                  {(showAll || permissions.includes("can_create")) && (
                    <button
                      className="main_btn"
                      onClick={() => navigate("/interview-appointments/create")}
                    >
                      {t("Add New")}
                    </button>
                  )}
                </div>
              </div>
              <Nav tabs className="one_line_parent mb-3">
                <NavItem>
                  <NavLink
                    className={
                      activeTab === "INTERVIEW_APPOINTMENT" ? "active" : ""
                    }
                    onClick={() => toggleTab("INTERVIEW_APPOINTMENT")}
                    disabled={loading}
                  >
                    {t("Interview Appointment")}
                  </NavLink>
                </NavItem>
                {(showAll ||
                  permissions.includes("can_view_invitations_lists")) && (
                  <NavItem>
                    <NavLink
                      className={activeTab === "INVITED_USER" ? "active" : ""}
                      onClick={() => toggleTab("INVITED_USER")}
                      disabled={loading}
                    >
                      {t("Invited Users")}
                    </NavLink>
                  </NavItem>
                )}
                <NavItem>
                  <NavLink
                    className={activeTab == 1 ? "active" : ""}
                    onClick={() => toggleTab(1)}
                    disabled={loading}
                  >
                    {t("Scheduled for Interview")}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={activeTab == 2 ? "active" : ""}
                    onClick={() => toggleTab(2)}
                    disabled={loading}
                  >
                    {t("Interview Completed")}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={activeTab == 3 ? "active" : ""}
                    onClick={() => toggleTab(3)}
                    disabled={loading}
                  >
                    {t("Cancelled")}
                  </NavLink>
                </NavItem>
              </Nav>
              {activeTab === "INTERVIEW_APPOINTMENT" &&
                (loading ? (
                  <Spinners />
                ) : (
                  <>
                    <Filters
                      filters={filters}
                      state={{ ...payload.filters, order_by: payload.order_by }}
                      setState={data => {
                        const { order_by, ...rest } = data
                        dispatch(
                          setPayload({
                            // order_by: Number(order_by),
                            filters: { ...payload.filters, ...rest },
                          })
                        )
                      }}
                      handleSearch={handleSearch}
                      handleReset={handleReset}
                    />
                    <Card className="user_personal_info theme_card overflow-x-auto">
                      <CardBody>
                        {loading && interviewsList.length === 0 ? (
                          <p>{t("No data found")}</p>
                        ) : (
                          <div className="table-responsive">
                            <Table className="table mb-0">
                              <thead className="table-light full_th">
                                <tr>
                                  <th>{t("S. No.")}</th>
                                  <th>{t("Title")}</th>
                                  <th>{t("Total Capacity")}</th>
                                  <th>{t("Total User Enrolled")}</th>
                                  <th>{t("Total Available Seat")}</th>
                                  {/* <th>{t("Education Qualification")}</th> */}
                                  {/* <th>{t("Employee Experience")}</th> */}
                                  <th>{t("Start Date")}</th>
                                  <th>{t("End Date")}</th>
                                  <th>{t("Action")}</th>
                                  {(showAll ||
                                    permissions.includes(
                                      "can_show_note_column"
                                    )) && <th>{t("Note")}</th>}
                                  {/* <th>{t("Gender")}</th> */}
                                  {/* <th>{t("Nationality")}</th> */}
                                  {/* <th>{t("Specialization")}</th> */}
                                </tr>
                              </thead>
                              <tbody>
                                {interviewsList?.map((item, index) => (
                                  <tr key={index}>
                                    <th scope="row">
                                      {(currentPage - 1) * perPage + index + 1}
                                    </th>
                                    <td>
                                      <p
                                        className="text-decoration-underline cursor-pointer"
                                        onClick={() => {
                                          if (
                                            showAll ||
                                            permissions.includes(
                                              "can_view_invitations_lists"
                                            )
                                          )
                                            navigate(
                                              `/interview-appointments/${item.uuid}/candidates`
                                            )
                                        }}
                                      >
                                        {item?.title}
                                      </p>
                                    </td>
                                    <td>{item?.total_capacity || "-"}</td>
                                    <td>{item?.total_user_enrolled}</td>
                                    <td>{item?.total_available_seat}</td>
                                    {/* <td>{item?.education_qualification?.name}</td> */}
                                    {/* <td>{item?.employee_experience}</td> */}
                                    <td>
                                      <p className="ltr fit_content">
                                        {item?.start_date} ~ {item?.start_time}
                                      </p>
                                    </td>
                                    <td>
                                      <p className="ltr fit_content">
                                        {item?.end_date} ~ {item?.end_time}
                                      </p>
                                    </td>
                                    <td onClick={handleStopPropagation}>
                                      <div className="d-flex gap-2 action_buttons">
                                        <Button
                                          color="primary"
                                          onClick={() => {
                                            setSelectedDescription({
                                              en: item.description,
                                              ar: item.description_ar,
                                            })
                                            setShowPreview(true)
                                          }}
                                          title={t("View Instructions")}
                                        >
                                          <Info />
                                        </Button>
                                        <Button
                                          color="secondary"
                                          onClick={() => {
                                            navigate(
                                              `/interview-appointments/update?id=${item?.uuid}`
                                            )
                                          }}
                                          title={t("Update")}
                                        >
                                          <Pencil />
                                        </Button>
                                        {item?.end_date &&
                                          moment(
                                            item.end_date,
                                            "DD-MM-YYYY"
                                          )?.isSameOrAfter(moment(), "day") && (
                                            <Button
                                              color="success"
                                              onClick={() => {
                                                toggle()
                                                setSelectedUuid(item?.uuid)
                                              }}
                                              title={t("Add Candidates")}
                                            >
                                              <Plus />
                                            </Button>
                                          )}
                                      </div>
                                    </td>
                                    {(showAll ||
                                      permissions.includes(
                                        "can_show_note_column"
                                      )) && (
                                      <td>
                                        <p style={{ minWidth: 300 }}>
                                          {item?.note}
                                        </p>
                                      </td>
                                    )}
                                    {/* <td>{item?.gender}</td>
                                <td>{item?.nationality?.name}</td>
                                <td>{item?.specialization?.name}</td> */}
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        )}
                      </CardBody>
                    </Card>

                    {!loading && interviewsList.length > 0 && (
                      <Paginations
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalCount={totalCount}
                        onPageChange={onPageChange}
                      />
                    )}
                  </>
                ))}

              {showPreview && (
                <HTMLModal
                  dangerousHTML={showCurrentLanguageDescription()}
                  isOpen={showPreview}
                  toggle={() => setShowPreview(false)}
                />
              )}
              {activeTab === "INVITED_USER" && (
                <InterviewAppointmentInvitedUsers />
              )}
              {(activeTab == 1 || activeTab == 2 || activeTab == 3) && (
                <InterviewTabs activeTab={activeTab} />
              )}
            </Col>
          </Row>
        </Container>
      </div>
      <Modal
        isOpen={modal}
        toggle={toggle}
        size="lg"
        className=""
        backdrop="static"
      >
        <ModalHeader toggle={toggle}>
          {t("Add Candidates In The Interview")}
        </ModalHeader>
        <ModalBody>
          <AddCandidateInPool
            toggle={toggle}
            selectedUuid={selectedUuid}
            getAllData={getAllData}
          />
        </ModalBody>
      </Modal>
    </React.Fragment>
  )
}

export default Interviews
